import React, { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import FullscreenImageDialog from "./FullviewImage";
import ImageCarousel from "./ImageCarousel";
import ProductBasicInfo from "./ProductBasicInfo";
import QuantitySelector from "./QuantitySelector";
import StoreInfo from "./StoreInfo";
import ProductDescription from "./ProductDescription";
import RelatedProductsCarousel from "./RelatedProductsCarousel";
import ProductVariantSelector from "./ProductVariantSelector";
import { useProductDetail } from "./useProductDetail";
import { ProductWithImages } from "../../types/Product";
import { ProductVariantAttributeWithValues, ProductVariant, ProductVariantDetail } from "../../types/ProductVariant";
import { AppConfigFields } from "../../types/appConfigEntities";
import { HttpProduct, HttpCategory } from "../../http";
import {
  buildVariantAttributeDisplay,
  pickVariantForSelectedAttributes,
  resolveInitialVariantSelection,
} from "../../utils/productVariantSelection";

interface BreadcrumbItem {
  label: string;
  href: string;
}

interface ProductDetailProps {
  productWithImages: ProductWithImages;
  variantAttributes?: ProductVariantAttributeWithValues[];
}

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

export default function ProductDetail({ productWithImages, variantAttributes = [] }: ProductDetailProps) {
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:768px)");
  const productInfo = productWithImages.producto;
  const productImages = useMemo(() => {
    const additional = productWithImages.imagenesAdicionales
      .slice()
      .sort((a, b) => a.posicion - b.posicion)
      .map((img) => img.urlImagen)
      .filter(Boolean);

    return [productInfo.urlImagenPrincipal, ...additional.filter((image) => image !== productInfo.urlImagenPrincipal)].filter(Boolean);
  }, [productWithImages.imagenesAdicionales, productInfo.urlImagenPrincipal]);

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [selectedVariantAttributes, setSelectedVariantAttributes] = useState<{ name: string; value: string }[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(minimumPurchase);

  const {
    selectedImage,
    fullscreenDialogOpen,
    nextImage,
    prevImage,
    setSelectedImage,
    openFullscreenDialog,
    closeFullscreenDialog,
  } = useProductDetail(productInfo, productImages);

  useEffect(() => {
    const variantStock = selectedVariant?.stock ?? productInfo.stock;
    const safeMin = variantStock != null ? Math.min(minimumPurchase, Math.max(variantStock, minimumPurchase)) : minimumPurchase;
    setQuantity(safeMin);
  }, [selectedVariant?.id, productInfo.id]);

  const handleQuantityChange = (value: number) => {
    const stock = selectedVariant?.stock ?? productInfo.stock;
    const safeValue = stock != null ? Math.min(Math.max(value, minimumPurchase), stock) : Math.max(value, minimumPurchase);
    setQuantity(safeValue);
  };

  const buildBreadcrumbs = async (categoryId: string, allCategories: any[], fromCategorySku?: string) => {
    const crumbs: BreadcrumbItem[] = [{ label: "Inicio", href: "/" }];

    if (fromCategorySku) {
      const categoryName = fromCategorySku.split("-").map((word) => word.toUpperCase()).join(" ");
      crumbs.push({ label: categoryName, href: `/categoria/${fromCategorySku}` });
      crumbs.push({ label: productInfo.nombre, href: `/productos/${productInfo.sku}` });
      return crumbs;
    }

    const buildCrumb = (catId: string | null) => {
      if (!catId) return;
      const cat = allCategories.find((c) => c.id === catId);
      if (cat) {
        buildCrumb(cat.idCategoriaPadre);
        crumbs.push({ label: cat.nombre, href: `/categoria/${cat.sku}` });
      }
    };

    buildCrumb(categoryId);
    crumbs.push({ label: productInfo.nombre, href: `/productos/${productInfo.sku}` });
    return crumbs;
  };

  useEffect(() => {
    const fetchBreadcrumbs = async () => {
      const fromCategorySku = location.state?.fromCategorySku as string | undefined;

      if (productInfo.categoriasIds && productInfo.categoriasIds.length > 0) {
        try {
          const allCategories = await HttpCategory.getAllCategories();
          const crumbs = await buildBreadcrumbs(productInfo.categoriasIds[0], allCategories, fromCategorySku);
          setBreadcrumbs(crumbs);
        } catch (error) {
          console.error("Error fetching categories for breadcrumbs:", error);
          setBreadcrumbs([{ label: "Inicio", href: "/" }, { label: productInfo.nombre, href: `/productos/${productInfo.sku}` }]);
        }
      } else {
        setBreadcrumbs([{ label: "Inicio", href: "/" }, { label: productInfo.nombre, href: `/productos/${productInfo.sku}` }]);
      }
    };

    fetchBreadcrumbs();
  }, [productInfo, location.state]);

  // Caso: producto sin atributos → carga variante default directamente
  useEffect(() => {
    if (!productInfo || !productInfo.idProductoVarianteDefault) return;
    if (variantAttributes && variantAttributes.length > 0) return;

    const loadVariant = async () => {
      try {
        const variantDetail = await HttpProduct.getProductVariantDetail(productInfo.idProductoVarianteDefault);
        setSelectedVariant(variantDetail.productoVariante);
        setSelectedVariantAttributes(
          variantDetail.productoVarianteAtributoValores.map((v) => ({
            name: v.nombreAtributo,
            value: v.nombreAtributoValor,
          }))
        );
      } catch (error) {
        console.error("Error loading default variant (no attributes):", error);
      }
    };

    loadVariant();
  }, [productInfo, variantAttributes]);

  // Caso: producto con atributos → inicializa selección con variante default
  useEffect(() => {
    if (!productInfo || !productInfo.idProductoVarianteDefault) return;
    if (!variantAttributes || variantAttributes.length === 0) return;

    const loadDefaultVariant = async () => {
      try {
        const [variantDetail, productVariantDetails] = await Promise.all([
          HttpProduct.getProductVariantDetail<ProductVariantDetail>(productInfo.idProductoVarianteDefault),
          HttpProduct.getProductVariantsByProductId<ProductVariantDetail>(productInfo.id).catch(() => []),
        ]);
        setSelectedVariant(variantDetail.productoVariante);

        const { selectedAttributes: initialSelected, selectedVariantAttributes: attrDisplay } =
          resolveInitialVariantSelection(
            variantAttributes,
            variantDetail,
            (productVariantDetails || []).map((detail) => detail.productoVariante)
          );
        setSelectedAttributes(initialSelected);

        // Derivar labels de la selección inicial (más confiable que el SP)
        setSelectedVariantAttributes(attrDisplay);
      } catch (error) {
        console.error("Error loading default variant:", error);
      }
    };

    loadDefaultVariant();
  }, [productInfo, variantAttributes]);

  // Caso: usuario cambia atributos → busca variante correspondiente
  useEffect(() => {
    if (!variantAttributes || variantAttributes.length === 0) return;

    const atributosSeleccionados = Object.keys(selectedAttributes);

    if (atributosSeleccionados.length !== variantAttributes.length) {
      setSelectedVariant(null);
      setSelectedVariantAttributes([]);
      return;
    }

    const requestBody = {
      idsProductoAtributos: atributosSeleccionados,
      idsProductoAtributoValores: atributosSeleccionados.map((attrId) => selectedAttributes[attrId]),
    };

    const fetchVariant = async () => {
      try {
        const response = await HttpProduct.getProductVariantByAttributesValues(productInfo.id, requestBody);

        const matchingVariant = pickVariantForSelectedAttributes(
          response || [],
          variantAttributes,
          selectedAttributes,
          productInfo.idProductoVarianteDefault
        );

        if (matchingVariant) {
          const detail = await HttpProduct.getProductVariantDetail(matchingVariant.id);
          setSelectedVariant(detail.productoVariante);

          // Derivar labels desde la selección del usuario (evita inconsistencias del SP)
          const attrDisplay = buildVariantAttributeDisplay(variantAttributes, selectedAttributes);
          setSelectedVariantAttributes(attrDisplay);
        } else {
          setSelectedVariant(null);
          setSelectedVariantAttributes([]);
        }
      } catch (error) {
        console.error("Error fetching variant:", error);
        setSelectedVariant(null);
        setSelectedVariantAttributes([]);
      }
    };

    fetchVariant();
  }, [selectedAttributes, variantAttributes, productInfo.id]);

  const handleAttributeChange = (attributeId: string, valueId: string) => {
    setSelectedAttributes((prev) => ({
      ...prev,
      [attributeId]: valueId,
    }));
  };

  const stock = selectedVariant?.stock ?? productInfo.stock;
  const price = selectedVariant?.precio ?? productInfo.precio ?? 0;
  const imageForCart = selectedVariant?.urlImagenPrincipal || productInfo.urlImagenPrincipal;

  return (
    <>
      <main style={{ padding: isMobile ? "16px 12px 60px" : "32px 32px 60px", position: "relative", zIndex: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.2fr 1fr", gap: isMobile ? 24 : 36 }}>
          <ImageCarousel
            images={productImages}
            productName={productInfo.nombre}
            productStock={stock}
            onImageClick={openFullscreenDialog}
            selectedImage={selectedImage}
            onImageSelect={setSelectedImage}
            onNextImage={nextImage}
            onPrevImage={prevImage}
          />

          <div style={{ display: "flex", flexDirection: "column", gap: 20, ...(isMobile ? {} : { position: "sticky", top: 100, alignSelf: "start" }) }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
              {breadcrumbs.map((breadcrumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={`${breadcrumb.label}-${index}`}>
                    {isLast ? (
                      <span className="pill" style={{ padding: "5px 10px", fontSize: 11, letterSpacing: "0.08em", fontWeight: 600, textTransform: "uppercase" }}>
                        {breadcrumb.label}
                      </span>
                    ) : (
                      <Link to={breadcrumb.href} className="pill" style={{ padding: "5px 10px", fontSize: 11, letterSpacing: "0.08em", fontWeight: 500, textDecoration: "none", color: "var(--ink-500)", textTransform: "uppercase" }}>
                        {breadcrumb.label}
                      </Link>
                    )}
                    {!isLast && <span style={{ opacity: .35, fontSize: 10, color: "var(--ink-400)" }}>›</span>}
                  </React.Fragment>
                );
              })}
            </div>

            <ProductBasicInfo product={productInfo} variant={selectedVariant} />

            <ProductVariantSelector
              variantAttributes={variantAttributes}
              selectedAttributes={selectedAttributes}
              onChange={handleAttributeChange}
              selectedVariant={selectedVariant}
            />

            {stock === null || stock > 0 ? (
              <QuantitySelector
                productId={productInfo.id.toString()}
                variantId={selectedVariant?.id}
                productName={productInfo.nombre}
                productPrice={price}
                productImage={imageForCart}
                quantity={quantity}
                stock={stock}
                onQuantityChange={handleQuantityChange}
                minQuantity={minimumPurchase}
                attributes={selectedVariantAttributes}
              />
            ) : (
              <button
                type="button"
                className="btn-primary"
                disabled
                style={{ width: "100%", opacity: .45, cursor: "not-allowed" }}
              >
                Sin stock
              </button>
            )}

            <StoreInfo />
            <ProductDescription product={productInfo} />
          </div>
        </div>

        <RelatedProductsCarousel
          currentProductId={productInfo.id}
          categoryId={productInfo.categoriasIds ? productInfo.categoriasIds[0] : undefined}
        />
      </main>

      <FullscreenImageDialog
        open={fullscreenDialogOpen}
        onClose={closeFullscreenDialog}
        images={productImages}
        productName={productInfo.nombre}
        selectedImage={selectedImage}
      />
    </>
  );
}
